<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  ArchiveBoxIcon,
  CheckCircleIcon,
  ClockIcon,
  UserIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/vue/24/outline';
import { IMaintenance } from '@/interfaces/entities/maintenance';
import { useI18n } from 'vue-i18n';
import { formatDateWithTime } from '@/utils/date/date-time';
import MaintenanceSectionFilter, {
  type IMaintenanceSectionFilters,
} from './MaintenanceSectionFilter.vue';

const { t } = useI18n();

const props = defineProps<{
  title: string;
  maintenances: IMaintenance[];
  section: string;
  borderColor: string;
}>();

const emit = defineEmits<{
  (e: 'select', maintenance: IMaintenance, section: string): void;
  (e: 'archive', maintenance: IMaintenance): void;
}>();

const filters = ref<IMaintenanceSectionFilters>({
  userId: null,
  priority: null,
  sensorId: null,
});

const filteredMaintenances = computed(() => {
  return props.maintenances.filter((maintenance) => {
    if (filters.value.userId != null && maintenance.user?.id !== filters.value.userId) {
      return false;
    }
    if (filters.value.priority != null && maintenance.priority !== filters.value.priority) {
      return false;
    }
    if (filters.value.sensorId != null && maintenance.sensor?.id !== filters.value.sensorId) {
      return false;
    }
    return true;
  });
});

const emptyMessage = computed(() => {
  const messages: Record<string, string> = {
    open: 'maintenance_user_listing.body.no_open_tasks',
    in_progress: 'maintenance_user_listing.body.no_in_progress_tasks',
    done: 'maintenance_user_listing.body.no_finished_tasks',
  };

  return messages[props.section];
});

const emptyStateMessage = computed(() => {
  if (props.maintenances.length > 0 && filteredMaintenances.value.length === 0) {
    return 'maintenance_user_listing.body.filters.no_results';
  }
  return emptyMessage.value;
});

function priorityBadgeClass(priority: string | undefined): string {
  switch (priority) {
    case 'high':
      return 'bg-red/10 text-red';
    case 'medium':
      return 'bg-meta-6/20 text-black';
    case 'low':
      return 'bg-darkGreen/10 text-darkGreen';
    default:
      return 'bg-slate-200 text-slate-700';
  }
}
</script>

<template>
  <section class="flex min-h-0 min-w-0 flex-col rounded-none border border-stroke bg-gray-50/50">
    <div class="sticky top-0 z-10 flex items-center gap-2 border-b border-stroke bg-gray-50 px-3 py-3">
      <span
        class="h-4 w-1.5 flex-shrink-0"
        :class="{
          'bg-orange-500': section === 'open',
          'bg-blue-500': section === 'in_progress',
          'bg-green': section === 'done',
        }"
      />
      <h2 class="text-base font-semibold text-slate-700">
        {{ title }}
      </h2>
      <span
        class="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-slate-200 px-1.5 text-xs font-medium text-slate-700"
      >
        {{ filteredMaintenances.length }}
      </span>
      <MaintenanceSectionFilter v-model="filters" :maintenances="maintenances" />
    </div>

    <div class="flex max-h-[min(70vh,40rem)] flex-col gap-3 overflow-y-auto p-3">
      <div
        v-for="maintenance in filteredMaintenances"
        :key="maintenance.id"
        class="cursor-pointer border-l-4 bg-white p-4 shadow-md transition hover:shadow-lg"
        :class="borderColor"
        @click="emit('select', maintenance, section)"
      >
        <div class="mb-2 flex items-start justify-between gap-2">
          <h3 v-if="maintenance.sensor" class="min-w-0 flex-1 text-base font-bold text-black">
            {{ maintenance.sensor.name }}
          </h3>
          <h3 v-else-if="maintenance.machine" class="min-w-0 flex-1 text-base font-bold text-black">
            {{ maintenance.machine.name }}
          </h3>
          <h3 v-else class="min-w-0 flex-1 text-base font-semibold text-gray-600">
            {{ t('maintenance_user_listing.modal.no_sensor_linked') }}
          </h3>
          <div class="flex shrink-0 items-center gap-1.5">
            <span
              v-if="maintenance.priority"
              class="px-2 py-0.5 text-xs font-medium"
              :class="priorityBadgeClass(maintenance.priority)"
            >
              {{ t(`maintenance_user_listing.priority.${maintenance.priority}`) }}
            </span>
            <button
              v-if="section === 'done'"
              type="button"
              class="inline-flex items-center justify-center p-1 text-gray-500 transition hover:bg-gray-100 hover:text-primary"
              :title="t('maintenance_user_listing.body.archive')"
              :aria-label="t('maintenance_user_listing.body.archive')"
              @click.stop="emit('archive', maintenance)"
            >
              <ArchiveBoxIcon class="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        <ul class="space-y-1.5 text-sm text-gray-600">
          <li v-if="maintenance.maintenance_type?.name" class="flex items-center gap-2">
            <WrenchScrewdriverIcon class="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
            <span class="truncate">{{ maintenance.maintenance_type.name }}</span>
          </li>
          <li v-if="maintenance.user?.name" class="flex items-center gap-2">
            <UserIcon class="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
            <span class="truncate">{{ maintenance.user.name }}</span>
          </li>
          <li v-if="section !== 'open' && maintenance.started_at" class="flex items-center gap-2">
            <ClockIcon class="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
            <span>{{ formatDateWithTime(maintenance.started_at) }}</span>
          </li>
          <li v-if="section === 'done' && maintenance.finished_at" class="flex items-center gap-2">
            <CheckCircleIcon class="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
            <span>{{ formatDateWithTime(maintenance.finished_at) }}</span>
          </li>
        </ul>
      </div>

      <p
        v-if="filteredMaintenances.length === 0 && emptyStateMessage"
        class="px-1 py-4 text-left text-sm font-medium text-gray-500"
      >
        {{ t(emptyStateMessage) }}
      </p>
    </div>
  </section>
</template>

<style scoped>
.cursor-pointer:hover {
  transform: scale(1.01);
  transition: transform 0.2s ease-in-out;
}
</style>
